// Batched writer for the player-scoped `behaviorTrackingEvents` log.
//
// Tajriba keeps every version of an attribute, on disk and in memory. Writing
// the whole log again for each event made the stored volume grow with the
// square of the event count: a participant records about 110 events, most of
// them window focus and blur, so a 340-participant batch wrote about 700 MB of
// log versions for about 10 MB of final logs, and the 4 GB server ran out of
// memory near 250 completers. Events now collect in memory and are written in
// one `set` per flush. The final array, its order and every event field are
// unchanged; only how often the array is written changes.
//
// A flush happens
//   (a) at once for paste, copy and cut, which the outside-tool filter depends
//       on, and when the page becomes hidden, the last reliable moment before a
//       tab closes;
//   (b) 30 s after the latest pending event, and at most 120 s after the oldest;
//   (c) when a tracking hook unmounts or its stage changes, and on pagehide;
//   (d) when a caller asks. The pre-lobby completion, the final-vote submit and
//       the exit survey flush before they advance, so the server's game-start
//       copy of the log holds every event detected before the participant
//       advanced, as it did when each event was written on its own.

export const BEHAVIOR_LOG_KEY = "behaviorTrackingEvents";
export const FLUSH_DEBOUNCE_MS = 30000;
export const FLUSH_MAX_WAIT_MS = 120000;

export function createBehaviorLogWriter({
  debounceMs = FLUSH_DEBOUNCE_MS,
  maxWaitMs = FLUSH_MAX_WAIT_MS,
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id),
} = {}) {
  // One entry per player, shared by every hook instance on the page. The
  // tracking hook mounts separately for the pre-lobby, the game and the exit
  // survey; a per-instance buffer could be overwritten at a mount boundary by a
  // new instance that read the log before the previous instance's last flush
  // reached it.
  const logs = new Map();

  const getEntry = (player) => {
    let entry = logs.get(player.id);
    if (!entry) {
      entry = { player, mirror: null, pending: [], timer: null, firstPendingAt: null };
      logs.set(player.id, entry);
    }
    entry.player = player;
    return entry;
  };

  const flushEntry = (entry) => {
    if (entry.timer !== null) {
      clearTimer(entry.timer);
      entry.timer = null;
    }
    if (entry.pending.length === 0) {
      return;
    }
    // Local mirror of the log, kept so the log only ever grows. A plain
    // read-modify-write loses events when a late server echo arrives:
    // Empirica's Attribute._update pushes the server payload into the local
    // BehaviorSubject unconditionally, so a `player.get` issued after that echo
    // can return an array SHORTER than what this client already wrote. The
    // mirror is reconciled against the server value on every write and the
    // longer of the two wins, so a reload, which starts with an empty mirror,
    // cannot truncate the log either.
    const remoteEvents = entry.player.get(BEHAVIOR_LOG_KEY);
    if (
      entry.mirror === null ||
      (Array.isArray(remoteEvents) && remoteEvents.length > entry.mirror.length)
    ) {
      entry.mirror = Array.isArray(remoteEvents) ? [...remoteEvents] : [];
    }
    entry.mirror = [...entry.mirror, ...entry.pending];
    entry.pending = [];
    entry.firstPendingAt = null;
    entry.player.set(BEHAVIOR_LOG_KEY, entry.mirror);
  };

  const schedule = (entry) => {
    if (entry.timer !== null) {
      clearTimer(entry.timer);
    }
    const current = now();
    const dueAt = Math.min(current + debounceMs, entry.firstPendingAt + maxWaitMs);
    entry.timer = setTimer(() => {
      entry.timer = null;
      flushEntry(entry);
    }, Math.max(0, dueAt - current));
  };

  return {
    enqueue(player, event, { immediate = false } = {}) {
      if (!player?.id) {
        return;
      }
      const entry = getEntry(player);
      entry.pending.push(event);
      if (entry.firstPendingAt === null) {
        entry.firstPendingAt = now();
      }
      if (immediate) {
        flushEntry(entry);
      } else {
        schedule(entry);
      }
    },
    flush(player) {
      const entry = player?.id ? logs.get(player.id) : null;
      if (entry) {
        flushEntry(entry);
      }
    },
    flushAll() {
      logs.forEach(flushEntry);
    },
  };
}

export const behaviorLogWriter = createBehaviorLogWriter();

// Called before a step or stage advance, so that every event detected before
// the participant advanced is written before the advance is.
export function flushBehaviorTracking() {
  behaviorLogWriter.flushAll();
}
